import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Card } from './ui/card';
import { chatbotAPI, type ChatMessage } from '@/lib/chatbot-api';
import { getChatCompletion, generateSessionId, type ChatContext } from '@/lib/openai-service';
import { config } from '@/lib/config';
import { RobotAvatar } from './RobotAvatar';

export function AIChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(generateSessionId());
  const [context, setContext] = useState<ChatContext>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      initializeChat();
    }
  }, [isOpen]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isLoading]);

  const initializeChat = async () => {
    const welcomeMessage: ChatMessage = {
      role: 'assistant',
      content: config.chatbot.welcomeMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages([welcomeMessage]);
    
    // Create conversation in database
    try {
      await chatbotAPI.createConversation({
        session_id: sessionId,
        language: '',
        messages: [welcomeMessage],
        status: 'active'
      });
      console.log('✅ Conversation created in database');
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }

    // Immediately ask for language preference
    setTimeout(async () => {
      const languageMessage: ChatMessage = {
        role: 'assistant',
        content: `What language would you prefer to speak? We support: ${config.chatbot.languages.join(', ')}`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, languageMessage]);
      
      try {
        await chatbotAPI.addMessage(sessionId, languageMessage);
      } catch (error) {
        console.error('Failed to add message:', error);
      }
    }, 1000);
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    
    try {
      await chatbotAPI.addMessage(sessionId, userMessage);
    } catch (error) {
      console.error('Failed to add user message:', error);
    }
    
    setInput('');
    setIsLoading(true);

    try {
      // Get AI response
      const response = await getChatCompletion([...messages, userMessage], context);
      
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      try {
        await chatbotAPI.addMessage(sessionId, assistantMessage);
      } catch (error) {
        console.error('Failed to add assistant message:', error);
      }

      // Extract information from conversation
      updateContextFromConversation(userMessage.content, response);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const updateContextFromConversation = async (userInput: string, aiResponse: string) => {
    const updates: Partial<ChatContext> = {};
    const conversationUpdates: any = {};

    console.log('🔍 Extracting info from:', userInput);

    // Detect language selection
    if (!context.language && config.chatbot.languages.some(lang => 
      userInput.toLowerCase().includes(lang.toLowerCase())
    )) {
      const selectedLang = config.chatbot.languages.find(lang => 
        userInput.toLowerCase().includes(lang.toLowerCase())
      );
      if (selectedLang) {
        updates.language = selectedLang;
        conversationUpdates.language = selectedLang;
        console.log('✅ Language detected:', selectedLang);
      }
    }

    // Extract name - improved patterns
    if (!context.customerName) {
      const namePatterns = [
        /my name is ([a-zA-Zα-ωΑ-Ω\s]+)/i,
        /i'm ([a-zA-Zα-ωΑ-Ω\s]+)/i,
        /i am ([a-zA-Zα-ωΑ-Ω\s]+)/i,
        /call me ([a-zA-Zα-ωΑ-Ω\s]+)/i,
        /this is ([a-zA-Zα-ωΑ-Ω\s]+)/i,
        /^([a-zA-Zα-ωΑ-Ω\s]{2,30})$/i, // Just a name by itself
      ];
      
      for (const pattern of namePatterns) {
        const match = userInput.match(pattern);
        if (match && match[1]) {
          const name = match[1].trim().replace(/\.$/, ''); // Remove trailing period
          // Exclude common words and languages
          const excludeWords = ['hello', 'hi', 'yes', 'no', 'ok', 'okay', 'thanks', 'thank you', 'please', 'english', 'greek', 'german', 'french', 'spanish', 'αγγλικα', 'ελληνικα'];
          if (name.length > 1 && name.length < 50 && !excludeWords.includes(name.toLowerCase())) {
            updates.customerName = name;
            conversationUpdates.customer_name = name;
            console.log('✅ Name detected:', name);
            break;
          }
        }
      }
      
      // Also try: if AI asks "what's your name" and user responds with just a word
      // But NOT if AI is asking about language
      if (!updates.customerName && 
          (aiResponse.toLowerCase().includes('name') || aiResponse.includes('λένε')) && 
          !aiResponse.toLowerCase().includes('language') &&
          !aiResponse.toLowerCase().includes('prefer')) {
        const simpleName = userInput.trim().replace(/[.,!?]$/, '');
        const words = simpleName.split(' ');
        if (words.length <= 3 && simpleName.length > 1 && simpleName.length < 50) {
          const excludeWords = ['hello', 'hi', 'yes', 'no', 'ok', 'okay', 'thanks', 'thank you', 'please', 'the', 'a', 'an', 'english', 'greek', 'german', 'french', 'spanish', 'αγγλικα', 'ελληνικα'];
          if (!excludeWords.includes(simpleName.toLowerCase())) {
            updates.customerName = simpleName;
            conversationUpdates.customer_name = simpleName;
            console.log('✅ Name detected (simple):', simpleName);
          }
        }
      }
    }

    // Extract country
    if (!context.country) {
      const countryPatterns = [
        /from ([a-zA-Zα-ωΑ-Ω\s]+)/i,
        /i'm from ([a-zA-Zα-ωΑ-Ω\s]+)/i,
        /i am from ([a-zA-Zα-ωΑ-Ω\s]+)/i,
        /living in ([a-zA-Zα-ωΑ-Ω\s]+)/i,
        /located in ([a-zA-Zα-ωΑ-Ω\s]+)/i,
        /apo ([a-zA-Zα-ωΑ-Ω\s]+)/i, // Greek "from"
      ];
      
      // Map common Greek/transliterated country names
      const countryMap: { [key: string]: string } = {
        'ellada': 'Greece',
        'ellas': 'Greece',
        'greece': 'Greece',
        'greek': 'Greece',
        'αγγλια': 'England',
        'anglia': 'England',
        'germania': 'Germany',
        'γερμανια': 'Germany',
        'gallia': 'France',
        'γαλλια': 'France',
      };
      
      for (const pattern of countryPatterns) {
        const match = userInput.match(pattern);
        if (match && match[1]) {
          let country = match[1].trim().replace(/[.,!?]$/, '');
          // Map to standard name if found
          const lowerCountry = country.toLowerCase();
          country = countryMap[lowerCountry] || country;
          
          if (country.length > 2 && country.length < 50) {
            updates.country = country;
            conversationUpdates.country = country;
            console.log('✅ Country detected:', country);
            break;
          }
        }
      }
      
      // If AI asks about country and user responds with just a country name
      if (!updates.country && (aiResponse.toLowerCase().includes('country') || aiResponse.toLowerCase().includes('where are you from') || aiResponse.toLowerCase().includes('χώρα') || aiResponse.toLowerCase().includes('από ποια'))) {
        let simpleCountry = userInput.trim().replace(/[.,!?]$/, '');
        // Map to standard name if found
        const lowerCountry = simpleCountry.toLowerCase();
        simpleCountry = countryMap[lowerCountry] || simpleCountry;
        
        if (simpleCountry.length > 2 && simpleCountry.length < 50) {
          updates.country = simpleCountry;
          conversationUpdates.country = simpleCountry;
          console.log('✅ Country detected (simple):', simpleCountry);
        }
      }
    }

    // Extract email
    const emailMatch = userInput.match(/[\w.-]+@[\w.-]+\.\w+/);
    if (emailMatch) {
      conversationUpdates.email = emailMatch[0];
      console.log('✅ Email detected:', emailMatch[0]);
    }

    // Extract phone (simple pattern)
    const phoneMatch = userInput.match(/[\d\s()+-]{10,}/);
    if (phoneMatch) {
      conversationUpdates.phone = phoneMatch[0].trim();
      console.log('✅ Phone detected:', phoneMatch[0].trim());
    }

    // Extract dates - improved patterns with Greek support
    const datePatterns = [
      /(\d{1,2}[/-]\d{1,2}[/-]\d{2,4})/g,
      /(\d{4}-\d{2}-\d{2})/g,
      // English months
      /(january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2}/gi,
      /\d{1,2}\s+(january|february|march|april|may|june|july|august|september|october|november|december)/gi,
      // Greek months (transliterated)
      /(ianouarios|fevrouarios|martios|aprilios|maios|iounios|ioulios|avgoustos|septemvrios|oktovrios|noemvrios|dekemvrios)\s+\d{1,2}/gi,
      /\d{1,2}\s+(ianouarios|fevrouarios|martios|aprilios|maios|iounios|ioulios|avgoustos|septemvrios|oktovrios|noemvrios|dekemvrios)/gi,
      // Common variations
      /(agousto|agosto)\s+/gi, // August variations
    ];
    
    const dates: string[] = [];
    
    // Check for "from X to Y" pattern
    const fromToPatterns = [
      /from\s+(\d{1,2})\s+(?:to|until|till|-)\s+(\d{1,2})/i,
      /apo\s+(\d{1,2})\s+(?:eos|mehri|eor)\s+(\d{1,2})/i, // Greek "from X to Y"
      /(\d{1,2})\s+(?:to|until|till|-|eos|mehri|eor)\s+(\d{1,2})/i,
    ];
    
    let foundFromTo = false;
    for (const pattern of fromToPatterns) {
      const match = userInput.match(pattern);
      if (match && match[1] && match[2]) {
        // Try to extract month from the message
        const monthMatch = userInput.match(/(january|february|march|april|may|june|july|august|september|october|november|december|agousto|avgoustos|agosto|ioulios|iounios|septemvrios|oktovrios|noemvrios|dekemvrios)/i);
        let month = monthMatch ? monthMatch[1].toLowerCase() : '';
        
        // Map Greek/variations to standard month
        const monthMap: { [key: string]: string } = {
          'agousto': 'August',
          'avgoustos': 'August',
          'agosto': 'August',
          'ioulios': 'July',
          'iounios': 'June',
          'septemvrios': 'September',
          'oktovrios': 'October',
          'noemvrios': 'November',
          'dekemvrios': 'December',
          'ianouarios': 'January',
          'fevrouarios': 'February',
          'martios': 'March',
          'aprilios': 'April',
          'maios': 'May',
        };
        
        month = monthMap[month] || month;
        
        // If we found a month, create proper dates
        if (month) {
          const year = new Date().getFullYear();
          dates.push(`${month} ${match[1]}, ${year}`);
          dates.push(`${month} ${match[2]}, ${year}`);
          foundFromTo = true;
          console.log(`✅ Date range detected: ${month} ${match[1]} to ${match[2]}`);
          break;
        }
      }
    }
    
    // If no "from-to" pattern found, use regular patterns
    if (!foundFromTo) {
      datePatterns.forEach(pattern => {
        const matches = userInput.match(pattern);
        if (matches) dates.push(...matches);
      });
    }

    if (dates.length >= 2) {
      conversationUpdates.check_in = dates[0];
      conversationUpdates.check_out = dates[1];
      updates.checkIn = dates[0];
      updates.checkOut = dates[1];
      console.log('✅ Check-in detected:', dates[0]);
      console.log('✅ Check-out detected:', dates[1]);
    } else if (dates.length === 1 && !context.checkIn) {
      conversationUpdates.check_in = dates[0];
      updates.checkIn = dates[0];
      console.log('✅ Check-in detected:', dates[0]);
    } else if (dates.length === 1 && context.checkIn && !context.checkOut) {
      conversationUpdates.check_out = dates[0];
      updates.checkOut = dates[0];
      console.log('✅ Check-out detected:', dates[0]);
    }

    // Update context and database
    if (Object.keys(updates).length > 0) {
      setContext(prev => ({ ...prev, ...updates }));
      console.log('📝 Context updated:', updates);
    }

    if (Object.keys(conversationUpdates).length > 0) {
      try {
        await chatbotAPI.updateConversation(sessionId, conversationUpdates);
        console.log('💾 Database updated:', conversationUpdates);
        
        // If we have email and phone, mark as completed
        const conversation = await chatbotAPI.getConversation(sessionId);
        if (conversation?.email && conversation?.phone) {
          await chatbotAPI.updateConversation(sessionId, { status: 'completed' });
          console.log('✅ Conversation marked as completed');
        }
      } catch (error) {
        console.error('Failed to update conversation:', error);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Chat Robot */}
      <div className="fixed bottom-6 right-6 z-50">
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="relative group cursor-pointer bg-transparent border-none p-0 hover:scale-110 transition-transform duration-300"
            aria-label="Open chat"
          >
            <RobotAvatar size={80} isAnimated={true} />
            {/* Online indicator */}
            <div className="absolute top-0 right-0 h-4 w-4 bg-green-400 rounded-full border-2 border-white animate-pulse shadow-lg" />
          </button>
        )}
      </div>

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 z-50 w-[380px] h-[600px] shadow-2xl flex flex-col animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-primary text-primary-foreground p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <RobotAvatar size={40} isAnimated={false} />
                <div className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-400 rounded-full border-2 border-white animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold">{config.chatbot.name}</h3>
                <p className="text-xs opacity-90">Online • Typically replies instantly</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="text-primary-foreground hover:bg-primary-foreground/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'} animate-in fade-in slide-in-from-bottom-2 duration-200`}
                >
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                    msg.role === 'user' ? 'bg-primary' : 'bg-muted'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="h-5 w-5 text-primary-foreground" />
                    ) : (
                      <div className="scale-75">
                        <RobotAvatar size={32} isAnimated={false} />
                      </div>
                    )}
                  </div>
                  <div className={`flex-1 max-w-[75%] ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                    <div className={`inline-block px-4 py-2 rounded-lg ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 px-1">
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-3 animate-in fade-in duration-200">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                    <div className="scale-75">
                      <RobotAvatar size={32} isAnimated={false} />
                    </div>
                  </div>
                  <div className="bg-muted px-4 py-3 rounded-lg">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
              {/* Auto-scroll anchor */}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                size="icon"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Powered by AI • Your data is secure
            </p>
          </div>
        </Card>
      )}
    </>
  );
}
